import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../../firebase';
import { sendEmailVerification, onAuthStateChanged, onIdTokenChanged, signOut } from 'firebase/auth';
import { doc, updateDoc, getDoc, collection, query, where, getDocs, serverTimestamp } from 'firebase/firestore';

const EmailVerificationPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [checkingVerification, setCheckingVerification] = useState(false);

  // portville_applications 자동 연결 함수
  const linkPortvilleApplications = async (user: any, userPhoneNumber: string) => {
    try {
      const applicationsRef = collection(db, 'portville_applications');
      const q = query(
        applicationsRef,
        where('status', 'in', ['pending', 'approved']),
        where('phoneNumber', '==', userPhoneNumber)
      );
      
      const querySnapshot = await getDocs(q);
      
      // uid가 비어있는 문서 찾기
      const emptyUidApplications = querySnapshot.docs.filter(doc => {
        const data = doc.data();
        return !data.uid || data.uid.trim() === '';
      });
      
      // 매칭되는 신청서가 있으면 uid 업데이트
      if (emptyUidApplications.length > 0) {
        const applicationDoc = emptyUidApplications[0]; // 첫 번째 매칭 신청서 사용
        
        // 사용자 프로필 정보 가져오기
        const userProfileRef = doc(db, 'user_profiles', user.uid);
        const userProfileSnap = await getDoc(userProfileRef);
        
        if (userProfileSnap.exists()) {
          const userData = userProfileSnap.data();
          
          // 신청서 업데이트
          await updateDoc(doc(db, 'portville_applications', applicationDoc.id), {
            uid: user.uid,
            name: userData.name || "",
            email: user.email || "",
            phoneNumber: userData.phoneNumber || "",
            organization: userData.organization || "",
            userLevel: userData.userLevel || 9,
            updatedAt: serverTimestamp()
          });
          
          console.log('portville_applications 자동 연결 완료:', applicationDoc.id);
          return true;
        }
      }
      
      return false;
    } catch (error) {
      console.error('portville_applications 자동 연결 중 오류:', error);
      return false;
    }
  };

  useEffect(() => {
    const handleEmailVerification = async (user: any) => {
      if (!user) {
        navigate('/signin');
        return;
      }

      // 사용자 토큰을 새로고침하여 최신 인증 상태 확인
      await user.reload();
      
      if (user.emailVerified) {
        setCheckingVerification(true);
        try {
          
          // 이메일 도메인 확인 및 회원등급 조정
          const emailDomain = user.email?.split('@')[1];
          let userLevel = 9; // 기본값

          if (emailDomain === 'busanpa.com') {
            userLevel = 3; // 공사 관계자
          } else if (emailDomain === 'bnfc.or.kr') {
            userLevel = 4; // 센터 관계자
          } else if (emailDomain === 'korea.kr') {
            userLevel = 7; // 공공기관 관계자
          }


          // Firestore에서 사용자 프로필 확인
          const userProfileRef = doc(db, 'user_profiles', user.uid);
          const userProfileSnap = await getDoc(userProfileRef);

          if (userProfileSnap.exists()) {
            const currentData = userProfileSnap.data();
            
            // 현재 userLevel이 10이고 role이 null인 경우에만 업데이트
            if (currentData.userLevel === 10 && !currentData.role) {
              const updateData = {
                uid: user.uid,
                userLevel: userLevel,
                role: 'user',
                status: 'active',
                updatedAt: new Date().toISOString()
              };


              try {
                await updateDoc(userProfileRef, updateData);
                
                // portville_applications 자동 연결 시도
                await linkPortvilleApplications(user, currentData.phoneNumber);
                
                // 메인 페이지로 이동
                navigate('/main');
              } catch (updateError) {
                console.error('프로필 업데이트 실패:', updateError);
                setError('프로필 업데이트에 실패했습니다. 페이지를 새로고침해주세요.');
              }
            } else {
              // 이미 인증된 사용자도 portville_applications 자동 연결 시도
              await linkPortvilleApplications(user, currentData.phoneNumber);
              
              // 메인 페이지로 이동
              navigate('/main');
            }
          } else {
            console.error('사용자 프로필이 존재하지 않습니다.');
            setError('사용자 프로필을 찾을 수 없습니다. 관리자에게 문의해주세요.');
          }
        } catch (err) {
          console.error('이메일 인증 처리 중 오류:', err);
          setError('인증 처리 중 오류가 발생했습니다. 페이지를 새로고침해주세요.');
        } finally {
          setCheckingVerification(false);
        }
      }
    };

    // Firebase Auth 상태 변화 리스너 설정
    const unsubscribeAuth = onAuthStateChanged(auth, handleEmailVerification);
    
    // ID 토큰 변화 리스너 설정 (이메일 인증 완료 시 토큰이 갱신됨)
    const unsubscribeToken = onIdTokenChanged(auth, handleEmailVerification);

    return () => {
      unsubscribeAuth();
      unsubscribeToken();
    };
  }, [navigate]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleResendEmail = async () => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      await sendEmailVerification(user);
      setCountdown(60); // 60초 카운트다운 시작
    } catch (err) {
      console.error('이메일 재전송 오류:', err);
      setError('이메일 재전송 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/signin');
    } catch (err) {
      console.error('로그아웃 오류:', err);
      setError('로그아웃 중 오류가 발생했습니다. 다시 시도해주세요.');
    }
  };

  return (
    <div className="w-full min-h-screen bg-white">
      {/* 본문 컨테이너 - 중앙 정렬 */}
      <div className="w-full max-w-[1920px] mx-auto px-4 md:px-0 relative pt-4 md:pt-8">
        <div className="w-full md:w-[760px] mx-auto">
          {/* 헤더 섹션 */}
          <div className="mt-4 md:mt-8">
            <div className="text-slate-800 text-2xl md:text-4xl font-extrabold font-['Pretendard'] leading-9">
              이메일 인증
            </div>
            <div className="mt-4 text-slate-700 text-base md:text-xl font-normal font-['Pretendard'] leading-9">
              가입하신 이메일 주소로 인증 메일을 발송했습니다
            </div>
          </div>

          {/* 구분선 */}
          <div className="w-full h-px mt-6 md:mt-8 outline outline-1 outline-offset-[-0.50px] outline-neutral-200"></div>

          {/* 본문 섹션 */}
          <div className="mt-6 md:mt-8 bg-white rounded-sm outline outline-1 md:outline-1 outline-slate-400 md:outline-slate-200">
            <div className="p-4 md:p-8">
              <h2 className="text-[#1e2c49] text-lg md:text-xl font-bold font-['Pretendard'] leading-9 mb-4">
                인증 안내
              </h2>
              <div className="w-full md:w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6 md:mb-8"></div>
            
            {checkingVerification ? (
              <div className="bg-blue-50 p-4 md:p-8 rounded border border-blue-200">
                <div className="flex items-center justify-center flex-wrap">
                  <div className="animate-spin rounded-full h-6 w-6 md:h-8 md:w-8 border-b-2 border-blue-500 mr-3"></div>
                  <span className="text-blue-700 text-sm md:text-lg font-medium font-['Pretendard']">
                    이메일 인증을 확인하고 있습니다...
                  </span>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 p-4 md:p-8 rounded border border-black/5">
                <ul className="space-y-3 md:space-y-4 text-slate-700 text-sm md:text-base font-normal font-['Pretendard']">
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2 flex-shrink-0">1.</span>
                    <span>입력하신 이메일 주소로 발송된 인증 메일을 확인해주세요.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2 flex-shrink-0">2.</span>
                    <span>이메일에 포함된 인증 링크를 클릭하여 인증을 완료해주세요.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2 flex-shrink-0">3.</span>
                    <span>인증이 완료되면 자동으로 다음 단계로 이동합니다.</span>
                  </li>
                </ul>
                <div className="mt-4 md:mt-6 p-3 md:p-4 bg-blue-50 rounded border border-blue-200">
                  <div className="flex items-center">
                    <div className="animate-pulse w-2 h-2 md:w-3 md:h-3 bg-blue-500 rounded-full mr-2 md:mr-3 flex-shrink-0"></div>
                    <span className="text-blue-700 text-xs md:text-sm font-medium font-['Pretendard']">
                      이메일 인증 완료를 실시간으로 감지하고 있습니다
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="px-4 md:px-8 pb-4 md:pb-8">
              <div className="bg-red-50 border-l-4 border-red-400 p-3 md:p-4 rounded">
                <p className="text-red-700 text-sm md:text-base">{error}</p>
              </div>
            </div>
          )}

          <div className="px-4 md:px-8 pb-4 md:pb-8">
            <button
              onClick={handleResendEmail}
              disabled={loading || countdown > 0 || checkingVerification}
              className={`w-full h-12 md:h-16 ${
                !loading && countdown === 0 && !checkingVerification
                  ? 'bg-[#155acb] hover:bg-[#155acb]/90'
                  : 'bg-gray-400 cursor-not-allowed'
              } rounded-sm`}
            >
              <div className="text-center text-white text-base md:text-lg font-bold font-['Pretendard'] leading-9">
                {loading ? '전송중...' : 
                 countdown > 0 ? `${countdown}초 후 재전송 가능` : 
                 checkingVerification ? '인증 확인 중...' :
                 '인증 메일 재전송'}
              </div>
            </button>

            <div className="mt-4 flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-4">
              <button
                onClick={() => navigate('/signin')}
                className="text-[#155acb] text-sm md:text-base font-bold font-['Pretendard'] hover:text-[#155acb]/90"
                disabled={checkingVerification}
              >
                로그인 페이지로 돌아가기
              </button>
              <span className="hidden sm:inline text-gray-400">|</span>
              <button
                onClick={handleLogout}
                className="text-red-600 text-sm md:text-base font-bold font-['Pretendard'] hover:text-red-600/90"
                disabled={checkingVerification}
              >
                로그아웃
              </button>
            </div>
          </div>
        </div>

        {/* 하단 여백 */}
        <div className="h-16 md:h-32"></div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPage; 
